/**
 * Iter 110 — Slip → PNG download utility.
 *
 * Captures a DOM node as a PNG and triggers a browser download.  Used
 * by both the user's manual BetSlipDrawer and the curated DailyBetslips
 * page so users can save / share their slip as an image.
 *
 * Why not server-side rendering?
 * - Client-side capture honours the live layout the user is looking at
 *   (theme, language, real-time leg statuses) without us having to
 *   re-render the React tree on the backend.
 * - No additional backend dependency on Pillow / headless Chrome.
 *
 * Caveats handled here:
 * - Pre-loads the brand logo so the captured image isn't missing it
 *   due to lazy-load / CORS races.
 * - Bumps `pixelRatio` to 2 for retina-quality output.
 * - Forces a solid background so dark-mode captures don't have a
 *   transparent backdrop that looks broken when shared on light
 *   chats (WhatsApp, etc.).
 */

import { toPng } from "html-to-image";

const _waitImagesLoaded = async (root) => {
  const imgs = root.querySelectorAll("img");
  await Promise.all(
    Array.from(imgs).map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        // 1.5s budget per image, then proceed regardless.
        const timer = setTimeout(resolve, 1500);
        img.addEventListener("load", () => { clearTimeout(timer); resolve(); }, { once: true });
        img.addEventListener("error", () => { clearTimeout(timer); resolve(); }, { once: true });
      });
    }),
  );
};

const _resolveBackground = (node) => {
  // Walk up until we find a non-transparent background — that's what
  // the user actually sees behind the card.
  let el = node;
  while (el && el !== document.documentElement) {
    const bg = window.getComputedStyle(el).backgroundColor;
    if (bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent") return bg;
    el = el.parentElement;
  }
  // Fallback to body's bg or pure white.
  return window.getComputedStyle(document.body).backgroundColor || "#ffffff";
};

const _injectWatermark = (node) => {
  // Ensure the parent provides a positioning context for our absolute
  // overlay.  We snapshot whatever the inline `position` value was so
  // we can restore it cleanly when the watermark is removed.
  const originalPosition = node.style.position;
  const computed = window.getComputedStyle(node).position;
  if (computed === "static") {
    node.style.position = "relative";
  }

  const wm = document.createElement("div");
  wm.dataset.pngWatermark = "1";
  wm.style.cssText = [
    "position:absolute",
    "bottom:6px",
    "right:10px",
    "z-index:10",
    "display:flex",
    "align-items:center",
    "gap:6px",
    "padding:4px 8px",
    "border-radius:9999px",
    "background:rgba(0,0,0,0.72)",
    "color:#fff",
    "font-size:10px",
    "font-weight:600",
    "letter-spacing:0.04em",
    "font-family:'Inter',system-ui,sans-serif",
    "pointer-events:none",
    "white-space:nowrap",
    "box-shadow:0 1px 4px rgba(0,0,0,0.25)",
  ].join(";");
  wm.innerHTML =
    '<span style="display:inline-block;width:14px;height:14px;border-radius:3px;background:#84cc16;color:#000;font-weight:900;text-align:center;line-height:14px;font-size:10px">N</span>' +
    '<span>Predicted by NDAKUBET · ndakubet.com</span>';
  node.appendChild(wm);

  return () => {
    wm.remove();
    node.style.position = originalPosition;
  };
};

/**
 * Capture `node` as a PNG and download it as `<filename>.png`.
 * Returns true on success, false on failure (errors are logged, never thrown).
 *
 * Iter 110 — Injects a "Predicted by NDAKUBET · ndakubet.com" watermark
 * pill in the bottom-right corner of the capture so the brand travels
 * along with every WhatsApp / Twitter share for free.  The watermark
 * is removed immediately after capture so the live UI is unaffected.
 */
export const downloadNodeAsPng = async (node, filename = "ndakubet-slip") => {
  if (!node) return false;
  let removeWatermark = null;
  try {
    await _waitImagesLoaded(node);

    const bg = _resolveBackground(node);
    removeWatermark = _injectWatermark(node);

    const dataUrl = await toPng(node, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: bg,
      // Skip any node tagged with `data-png-skip="1"` — used to hide
      // the download button itself from the captured image.
      filter: (n) => {
        if (n?.dataset?.pngSkip === "1") return false;
        return true;
      },
    });

    const link = document.createElement("a");
    // Iter 114 — Don't auto-append today's date to the filename; the
    // caller supplies the slip's actual date (which may differ from
    // today for historical slip downloads).  Previously this produced
    // names like `ndakubet-slip-2026-05-11-2026-05-11.png`.
    link.download = filename.endsWith(".png") ? filename : `${filename}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    link.remove();
    return true;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("[slipToPng] capture failed:", e);
    return false;
  } finally {
    if (removeWatermark) {
      try { removeWatermark(); } catch (_) {}
    }
  }
};
